import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ChevronRight, Frown, ChevronLeft } from "lucide-react";
import {
  stateByCode,
  getStateUrl,
  getCityUrl,
  getListingBySlug,
  slugifyCity,
  slugify,
  STATES,
  LISTINGS,
} from "../constants";
import {
  getCourses,
  getCoursesSync,
  getCityContent,
  getCityContentSync,
  generateDefaultCityContent,
  getStateContent,
  getStateContentSync,
  generateDefaultStateContent,
} from "../api";
import { ListingCard, CityListingCard } from "../components/CourseCard";
import FormattedText from "../components/FormattedText";
import SEO from "../components/SEO";
import NotFound from "./NotFound";
import { buildCollectionSchema, buildFaqSchema, buildBreadcrumbSchema } from "../utils/seoSchemas";

const COURSES_PER_PAGE = 9;

const StatePage = () => {
  const params = useParams();
  const rawListingSlug = params.listingSlug || params["*"] || null;
  const rawStateCode = params.stateCode;
  const rawCitySlug = params.citySlug;

  // 1. Resolve listing if a listingSlug is present
  const listing = useMemo(() => {
    if (rawListingSlug) {
      return getListingBySlug(rawListingSlug);
    }
    return null;
  }, [rawListingSlug]);

  // 2. Resolve effectiveStateCode
  const effectiveStateCode = useMemo(() => {
    if (listing?.stateCode) return listing.stateCode;
    if (rawStateCode) return rawStateCode;
    // Fallback: match by citySlug or listingSlug
    const searchSlug = (rawCitySlug || rawListingSlug || "").toLowerCase();
    if (searchSlug) {
      const allCourses = getCoursesSync();
      const match = allCourses.find((c) => {
        const cSlug = (c.citySlug || slugifyCity(c.city || "")).toLowerCase();
        return cSlug === searchSlug || searchSlug.includes(cSlug);
      });
      if (match?.state) return match.state;

      const listingMatch = LISTINGS.find(
        (l) => l.citySlug.toLowerCase() === searchSlug || l.slug.toLowerCase() === searchSlug
      );
      if (listingMatch?.stateCode) return listingMatch.stateCode;
    }
    return "al"; // Default fallback
  }, [listing, rawStateCode, rawCitySlug, rawListingSlug]);

  const state = stateByCode(effectiveStateCode);

  // 3. Resolve effectiveCitySlug
  const effectiveCitySlug = useMemo(() => {
    if (listing?.citySlug) return listing.citySlug;
    if (rawCitySlug) return rawCitySlug;
    if (rawListingSlug) {
      const clean = rawListingSlug.toLowerCase().replace(/^\/+|\/+$/g, "");
      const match = getListingBySlug(clean);
      if (match) return match.citySlug;
      return clean;
    }
    return null;
  }, [listing, rawCitySlug, rawListingSlug]);

  const isCityPage = Boolean(effectiveCitySlug);

  // 4. State courses state with instant 0ms synchronous initial load
  const [asyncCourses, setAsyncCourses] = useState(null);
  const stateCourses = useMemo(() => {
    if (asyncCourses) return asyncCourses;
    if (state?.code) return getCoursesSync({ state: state.code });
    return getCoursesSync();
  }, [asyncCourses, state?.code]);

  // 5. Determine City Name safely
  const cityName = useMemo(() => {
    if (listing?.cityName) return listing.cityName;
    if (!effectiveCitySlug) return "";
    const clean = effectiveCitySlug.toLowerCase();
    const match = (stateCourses || []).find((c) => {
      const cSlug = (c.citySlug || slugifyCity(c.city || "")).toLowerCase();
      return cSlug === clean;
    });
    if (match?.city) return match.city;
    return effectiveCitySlug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }, [listing, effectiveCitySlug, stateCourses]);

  // 6. City & State content state (Admin editable)
  const [cityContent, setCityContent] = useState(() => {
    if (!state?.code || !effectiveCitySlug) return null;
    return getCityContentSync(state.code, effectiveCitySlug);
  });

  const [stateContent, setStateContent] = useState(() => {
    if (!state?.code || effectiveCitySlug) return null;
    return getStateContentSync(state.code);
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Fetch live courses and city/state content
  useEffect(() => {
    if (state?.code) {
      getCourses({ state: state.code })
        .then((data) => {
          if (data && data.length > 0) setAsyncCourses(data);
        })
        .catch(() => {});

      if (effectiveCitySlug) {
        const syncCt = getCityContentSync(state.code, effectiveCitySlug);
        setCityContent(syncCt);

        getCityContent(state.code, effectiveCitySlug)
          .then((ct) => {
            if (ct) setCityContent(ct);
          })
          .catch(() => {});
      } else {
        setCityContent(null);
        const syncSt = getStateContentSync(state.code);
        setStateContent(syncSt);

        getStateContent(state.code)
          .then((st) => {
            if (st) setStateContent(st);
          })
          .catch(() => {});
      }
    }
  }, [state?.code, effectiveCitySlug]);

  // Reset pagination on route change
  useEffect(() => {
    setCurrentPage(1);
  }, [effectiveCitySlug, rawStateCode, rawListingSlug]);

  // Disable all short state code URLs (e.g. /state/oh/sandusky, /state/al, /state/oh)
  // so ONLY full canonical live URLs (e.g. /state/ohio/sandusky, /state/alabama) work.
  const isInvalidStateRoute = useMemo(() => {
    if (rawStateCode && state) {
      const canonicalSlug = slugify(state.name);
      return rawStateCode.toLowerCase() !== canonicalSlug;
    }
    return false;
  }, [rawStateCode, state]);

  if (!state || isInvalidStateRoute) {
    return <NotFound />;
  }

  const all = stateCourses || [];
  const courses = isCityPage
    ? all.filter((c) => {
        const cCity = c.city ? String(c.city).toLowerCase() : "";
        const cSlug = (c.citySlug || slugifyCity(c.city || "")).toLowerCase();
        const targetSlug = (effectiveCitySlug || "").toLowerCase();
        const targetName = (cityName || "").toLowerCase();
        return (
          cSlug === targetSlug ||
          (targetName && cCity === targetName) ||
          (cCity && cCity === targetSlug.replace(/-/g, " "))
        );
      })
    : all;

  const loading = !stateCourses || stateCourses.length === 0;
  const cities = [...new Set(all.map((c) => c.city).filter(Boolean))].sort();

  // City calculations
  const courseCount = courses.length;
  const avgRating = courseCount > 0
    ? (courses.reduce((sum, c) => sum + Number(c.rating || 0), 0) / courseCount).toFixed(1)
    : "4.5";

  const sortedByRating = [...courses].sort(
    (a, b) => (b.rating - a.rating) || (b.reviewCount - a.reviewCount)
  );
  const topCourse = sortedByRating[0];

  // Default content generator if no custom content saved yet
  const effectiveContent =
    cityContent ||
    (isCityPage ? generateDefaultCityContent(state.name, cityName, courses) : null);

  // Pagination calculations
  const totalPages = Math.ceil(courseCount / COURSES_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * COURSES_PER_PAGE;
  const endIndex = Math.min(startIndex + COURSES_PER_PAGE, courseCount);
  const paginatedCourses = courses.slice(startIndex, endIndex);

  // ==================== CITY PAGE ====================
  if (isCityPage) {
    const pageTitle = effectiveContent?.customTitle || `Mini Golf Courses in ${cityName}, ${state.name}`;
    const leadText = effectiveContent?.leadParagraph ||
      `Looking for mini golf in ${cityName}, ${state.name}? Browse ${courseCount} courses with an average rating of ${avgRating} stars.${topCourse ? ` ${topCourse.name} is the top-rated spot, earning ${topCourse.rating} stars from players.` : ""}`;
    const rawParagraphs = effectiveContent?.contentParagraphs;
    const paragraphs = Array.isArray(rawParagraphs) && rawParagraphs.length > 0
      ? rawParagraphs.filter(Boolean)
      : (typeof rawParagraphs === "string" && rawParagraphs.trim()
          ? rawParagraphs.split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
          : [
              `Mini golf in ${cityName} offers a fantastic variety of fun for families, friends, date nights, and competitive putters. Across the area, you'll find everything from classic obstacles and windmill challenges to elaborately themed adventure greens, waterfalls, and climate-controlled indoor layouts that let you putt comfortably in any season. Our directory lists ${courseCount} top-rated mini golf spots around ${cityName} with an average visitor rating of ${avgRating} stars based on player reviews. Browse the full list below to compare course photos, ratings, hours, and locations before planning your visit.`
            ]);

    const cityCanonicalUrl =
      listing?.url || `https://minigolfalabama.com/state/${slugify(state.name)}/${effectiveCitySlug}`;
    const cityFaqs = [
      {
        q: `How many mini golf courses are located in ${cityName}, ${state.name}?`,
        a: `There are currently ${courseCount} mini golf courses listed in ${cityName}, ${state.name} with an average player rating of ${avgRating} stars.`
      },
      {
        q: `What is the top-rated mini golf course in ${cityName}?`,
        a: topCourse
          ? `${topCourse.name} is the highest-rated mini golf destination in ${cityName}, holding a ${topCourse.rating}-star rating from ${topCourse.reviewCount || 0} reviews.`
          : `Explore all top-rated mini golf venues in ${cityName} listed above.`
      },
      {
        q: `Are indoor mini golf courses available in ${cityName}?`,
        a: `Yes, many destinations in ${cityName} and surrounding areas feature climate-controlled indoor courses, blacklight glow-in-the-dark putt putt, and outdoor adventure greens.`
      }
    ];

    const citySchemas = [
      buildCollectionSchema(pageTitle, leadText, courses, cityCanonicalUrl),
      buildFaqSchema(cityFaqs),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: state.name, url: getStateUrl(state.code) },
        { name: cityName, url: cityCanonicalUrl },
      ]),
    ];

    return (
      <div className="min-h-screen bg-[#0d0e12] text-gray-100 pb-20 font-sans" data-testid="city-page">
        <SEO
          title={`${pageTitle} | Best Indoor & Outdoor Mini Golf Near Me`}
          description={leadText.slice(0, 160)}
          canonicalUrl={cityCanonicalUrl}
          ogType="website"
          schema={citySchemas}
        />
        {/* Header Breadcrumbs & Main Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-gray-600">/</span>
            <Link to={getStateUrl(state.code)} className="hover:text-white transition-colors">
              {state.name}
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-gray-200 font-medium">{cityName}</span>
          </nav>

          {/* Main Title */}
          <h1
            className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight"
            data-testid="city-title"
          >
            {pageTitle}
          </h1>

          {/* Summary Stats Bar */}
          <div className="mt-6 bg-[#181a22] border border-gray-800/80 rounded-xl px-5 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-300 font-medium shadow-md">
            <span className="text-white font-bold">{courseCount} Courses</span>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <span>
              Average Rating: <strong className="text-white font-semibold">{avgRating}</strong>
            </span>
            <span className="text-gray-600 hidden sm:inline">|</span>
            {topCourse && (
              <span>
                Highest Rated: <strong className="text-white font-semibold">{topCourse.name} ({topCourse.rating} stars)</strong>
              </span>
            )}
          </div>

          {/* Intro Lead & Narrative Paragraphs */}
          <div className="mt-8 space-y-4 text-[15px] sm:text-base leading-relaxed text-gray-300 max-w-5xl">
            <FormattedText
              text={leadText}
              paragraphClassName="text-gray-200 font-normal break-words leading-relaxed"
              linkClassName="text-emerald-400 hover:text-emerald-300 underline font-semibold transition-colors break-words"
            />
            {paragraphs.map((p, idx) => (
              <FormattedText
                key={idx}
                text={p}
                paragraphClassName="text-gray-400 font-normal break-words leading-relaxed"
                linkClassName="text-emerald-400 hover:text-emerald-300 underline font-semibold transition-colors break-words"
              />
            ))}
          </div>

          {/* Courses Found Sub-header */}
          <div className="mt-12 pt-6 border-t border-gray-800 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              {courseCount} Courses Found
            </h2>
            {courseCount > 0 && (
              <div className="text-sm text-gray-400">
                Showing {startIndex + 1}-{endIndex} of {courseCount} courses
              </div>
            )}
          </div>

          {/* Courses Grid */}
          {courseCount === 0 ? (
            <div className="mt-8 text-center py-16 bg-[#181a20] rounded-2xl border border-gray-800 p-8">
              <Frown className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white">No courses listed in {cityName} yet</h3>
              <p className="text-gray-400 text-sm mt-1">
                Explore nearby courses in {state.name} or browse our top rated destinations.
              </p>
              <Link
                to={getStateUrl(state.code)}
                className="inline-block mt-5 px-6 py-2.5 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl transition-colors"
              >
                View All {state.name} Courses
              </Link>
            </div>
          ) : (
            <div
              className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="city-course-grid"
            >
              {paginatedCourses.map((course) => (
                <CityListingCard key={course.id} course={course} />
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-semibold rounded-xl bg-[#181a20] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setCurrentPage(num)}
                    className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${
                      currentPage === num
                        ? "bg-[#e5243b] text-white shadow-md shadow-red-900/40"
                        : "bg-[#181a20] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-600"
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-semibold rounded-xl bg-[#181a20] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== STATE PAGE ====================
  const effectiveStateContent =
    stateContent || generateDefaultStateContent(state.name, courses);

  const statePageTitle = effectiveStateContent?.customTitle || `Mini Golf Courses in ${state.name}`;
  const stateLeadText = effectiveStateContent?.leadParagraph ||
    `Looking for the best mini golf courses in ${state.name}? Explore ${courses.length} top-rated putt putt locations across ${cities.length} cities with an average visitor rating of ${avgRating} stars.${topCourse ? ` ${topCourse.name} leads the state with a ${topCourse.rating}-star rating.` : ""}`;
  const rawStateParagraphs = effectiveStateContent?.contentParagraphs;
  const stateParagraphs = Array.isArray(rawStateParagraphs) && rawStateParagraphs.length > 0
    ? rawStateParagraphs.filter(Boolean)
    : (typeof rawStateParagraphs === "string" && rawStateParagraphs.trim()
        ? rawStateParagraphs.split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
        : [
            `Whether you're planning a family weekend outing, a memorable date night, or looking for indoor glowing blacklight courses, ${state.name} offers a diverse range of miniature golf adventures. From coastal outdoor courses featuring pirate ships and cascading waterfalls to climate-controlled indoor fun centers with arcade games and snacks, there's a course for every skill level.`,
            `Browse our comprehensive state directory below to compare player ratings, admission prices, course hours, and photos before heading out to play.`
          ]);

  const stateCanonicalUrl = `https://minigolfalabama.com/state/${slugify(state.name)}`;
  const stateSchemas = [
    buildCollectionSchema(
      statePageTitle,
      stateLeadText,
      courses,
      stateCanonicalUrl
    ),
    buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: state.name, url: getStateUrl(state.code) },
    ]),
  ];

  return (
    <div className="min-h-screen bg-[#0d0e12] text-gray-100 pb-20 font-sans" data-testid="state-page">
      <SEO
        title={`${statePageTitle} | Best Indoor & Outdoor Mini Golf Near Me`}
        description={stateLeadText.slice(0, 160)}
        canonicalUrl={stateCanonicalUrl}
        ogType="website"
        schema={stateSchemas}
      />
      {/* Header Breadcrumbs & Main Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span className="text-gray-600">/</span>
          <span className="text-gray-200 font-medium">{state.name}</span>
        </nav>

        {/* Main Title */}
        <h1
          className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight"
          data-testid="state-title"
        >
          {statePageTitle}
        </h1>

        {/* Summary Stats Bar */}
        <div className="mt-6 bg-[#181a22] border border-gray-800/80 rounded-xl px-5 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-300 font-medium shadow-md">
          <span className="text-white font-bold">{courseCount} Courses</span>
          <span className="text-gray-600 hidden sm:inline">|</span>
          <span>{cities.length} Cities</span>
          <span className="text-gray-600 hidden sm:inline">|</span>
          <span>
            Average Rating: <strong className="text-white font-semibold">{avgRating}</strong>
          </span>
          <span className="text-gray-600 hidden sm:inline">|</span>
          {topCourse && (
            <span>
              Highest Rated: <strong className="text-white font-semibold">{topCourse.name} ({topCourse.rating} stars)</strong>
            </span>
          )}
        </div>

        {/* City Filter Pills */}
        {cities.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {cities.map((city) => (
              <Link
                key={city}
                to={getCityUrl(state.code, city)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1e2029] border border-gray-800 rounded-xl text-xs font-semibold text-gray-200 hover:border-[#e5243b] hover:text-white transition-all shadow-sm"
              >
                <MapPin className="w-3 h-3 text-[#e5243b]" /> {city}
              </Link>
            ))}
          </div>
        )}

        {/* Intro Lead & Narrative Paragraphs */}
        <div className="mt-8 space-y-4 text-[15px] sm:text-base leading-relaxed text-gray-300 max-w-5xl">
          <FormattedText
            text={stateLeadText}
            paragraphClassName="text-gray-200 font-normal break-words leading-relaxed"
            linkClassName="text-emerald-400 hover:text-emerald-300 underline font-semibold transition-colors break-words"
          />
          {stateParagraphs.map((p, idx) => (
            <FormattedText
              key={idx}
              text={p}
              paragraphClassName="text-gray-400 font-normal break-words leading-relaxed"
              linkClassName="text-emerald-400 hover:text-emerald-300 underline font-semibold transition-colors break-words"
            />
          ))}
        </div>

        {/* Courses Found Sub-header */}
        <div className="mt-12 pt-6 border-t border-gray-800 flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            {courseCount} Courses Found in {state.name}
          </h2>
          {courseCount > 0 && (
            <div className="text-sm text-gray-400">
              Showing {startIndex + 1}-{endIndex} of {courseCount} courses
            </div>
          )}
        </div>

        {/* Courses Grid */}
        {courseCount === 0 ? (
          <div className="mt-8 text-center py-16 bg-[#181a20] rounded-2xl border border-gray-800 p-8">
            <Frown className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white">No courses listed in {state.name} yet</h3>
            <p className="text-gray-400 text-sm mt-1">
              We haven't added courses for this area yet. Check back soon!
            </p>
            <Link
              to="/"
              className="inline-block mt-5 px-6 py-2.5 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl transition-colors"
            >
              Back to Home
            </Link>
          </div>
        ) : (
          <div
            className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="state-course-grid"
          >
            {paginatedCourses.map((course) => (
              <CityListingCard key={course.id} course={course} />
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-semibold rounded-xl bg-[#181a20] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setCurrentPage(num)}
                  className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${
                    currentPage === num
                      ? "bg-[#e5243b] text-white shadow-md shadow-red-900/40"
                      : "bg-[#181a20] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-600"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-semibold rounded-xl bg-[#181a20] border border-gray-800 text-gray-300 hover:text-white hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatePage;
